'use client'

import * as React from 'react'
import { useTranslations } from 'next-intl'
import { useQueryClient } from '@tanstack/react-query'
import { tw } from 'utils/classnames'
import { TickIcon } from 'primitives/icons'
import { useIsLoggedInQuery } from 'auth/hooks'
import { useUser } from '@repo/cf-user/hooks/use-user-data'
import { useUserAuthModal } from '@repo/user-auth-dialog/user-auth-dialog-context'
import { SkeletonItem } from 'components/skeleton-item'
import { AnimatePresence, motion } from 'framer-motion'
import { Button } from '@repo/design-system-cf/components/button'
import { AmplitudeEvents, sendAmplitudeEvent } from '@repo/analytics/amplitude/utils'

import { CrossIcon } from '@web/app/[locale]/clubhouse/assets/icons'
import {
  useClubhousesJoinMutation,
  useClubhousesLeaveMutation,
  useClubhousesIsMemberQuery,
  useClubhousesGetDetailsQuery,
} from '@web/gen/clubhouses'
import { ClubhouseSettingsPopover } from './clubhouse-settings-popover'

const transitionAnim = {
  initial: { opacity: 0, scale: 0.95 },
  animate: { opacity: 1, scale: 1 },
  exit: { opacity: 0, scale: 0.95 },
  transition: { duration: 0.2 },
}

// oxlint-disable-next-line complexity
export function JoinClubhouseButton({ slug }: { slug: string }) {
  const t = useTranslations('pages.clubhouse.topbanner')
  const queryClient = useQueryClient()
  const { openModal } = useUserAuthModal()
  const isLoggedInQuery = useIsLoggedInQuery()
  const user = useUser()
  const isMemberQuery = useClubhousesIsMemberQuery(
    { input: { clubhouseSlug: slug } },
    {
      enabled: isLoggedInQuery.data,
    },
  )
  const { data: clubhouseName } = useClubhousesGetDetailsQuery(
    {
      input: {
        clubhouseSlug: slug,
      },
    },
    {
      select: data => data?.clubhousesGetDetails?.clubhouse?.name,
    },
  )
  const isMember = isMemberQuery.data?.clubhousesIsClubhouseMember.isMember
  const [isJoined, setIsJoined] = React.useState(isMember || false)
  // This is a workaround to the issue of transitions not firing when the element is hidden behind overflow
  const [showJoinedLabel, setShowJoinedLabel] = React.useState(false)

  const [enableSignOff, setEnableSignOff] = React.useState(false)
  const [isHovered, setIsHovered] = React.useState(false)
  const [justJoined, setJustJoined] = React.useState(false)

  const joinMutation = useClubhousesJoinMutation()
  const leaveMutation = useClubhousesLeaveMutation()

  const handleClick = () => {
    if (!isLoggedInQuery.data) {
      openModal('register')
      return
    }
    if (isJoined) {
      leaveMutation.mutateAsync(
        { input: { clubhouseSlug: slug } },
        {
          onSuccess: data => {
            if (data?.clubhousesLeave?.Success) {
              sendAmplitudeEvent(AmplitudeEvents.LEFT_CLUBHOUSE, {
                title: clubhouseName,
              })
              queryClient.invalidateQueries({
                queryKey: ['clubhousesIsMember', { input: { clubhouseSlug: slug } }],
              })

              queryClient.invalidateQueries({
                queryKey: ['clubhousesFromUser', { input: { userId: user.data?.id } }],
              })
              setIsJoined(false)
              setJustJoined(false)
            }
          },
        },
      )
    } else {
      joinMutation.mutateAsync(
        { input: { clubhouseSlug: slug } },
        {
          onSuccess: data => {
            if (data?.clubhousesJoin?.Success) {
              sendAmplitudeEvent(AmplitudeEvents.JOINED_CLUBHOUSE, {
                title: clubhouseName,
              })
              queryClient.invalidateQueries({
                queryKey: ['clubhousesIsMember', { input: { clubhouseSlug: slug } }],
              })
              queryClient.invalidateQueries({
                queryKey: ['clubhousesFromUser', { input: { userId: user.data?.id } }],
              })
              setIsJoined(true)
              setJustJoined(true)
            }
          },
        },
      )
    }
  }

  React.useEffect(() => {
    if (isMember) {
      setIsJoined(isMember)
    }
  }, [isMemberQuery.data, isMember])

  React.useEffect(() => {
    if (isJoined) {
      setTimeout(() => setShowJoinedLabel(true), 900)
      setTimeout(() => setEnableSignOff(true), 1500)
    } else {
      setShowJoinedLabel(false)
      setEnableSignOff(false)
    }
  }, [isJoined])

  if (isMemberQuery.isPending) {
    return (
      <div className="flex gap-2">
        <SkeletonItem className="h-9 w-[173px]" />
        <SkeletonItem className="size-9" />
      </div>
    )
  }

  return (
    <AnimatePresence mode="wait">
      <div className="flex items-center gap-2">
        {!isJoined || justJoined ? (
          <Button
            onClick={handleClick}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >
            <motion.div
              key="join"
              {...transitionAnim}
              className={tw(
                'text-14 group flex h-9 w-[173px] flex-col items-center overflow-hidden border border-transparent px-3 py-0 transition-colors duration-500',
              )}
            >
              <span
                className={tw(
                  'flex h-9 shrink-0 flex-row items-center justify-center',
                  isJoined && !enableSignOff && 'animate-[slideUp_300ms_ease-in-out_forwards]',
                  enableSignOff && '-translate-y-[100%]',
                )}
              >
                {t('cta')}
              </span>
              <span
                className={tw(
                  'flex h-9 shrink-0 flex-row items-center justify-center gap-1',
                  isJoined && !enableSignOff && 'animate-[slideUp_300ms_ease-in-out_forwards]',
                  enableSignOff && '-translate-y-[100%]',
                )}
              >
                {enableSignOff && isHovered ? (
                  <CrossIcon />
                ) : (
                  <TickIcon
                    className={tw(
                      'size-5 [clip-path:inset(0_100%_0_0)]',
                      isJoined &&
                        !enableSignOff &&
                        'animate-[rollRight_250ms_ease-in-out_forwards]',
                      enableSignOff && '[clip-path:inset(0)]',
                    )}
                    style={{ animationDelay: '300ms' }}
                  />
                )}
                <span
                  className={tw(
                    'w-0',
                    'inline-block overflow-hidden whitespace-nowrap text-left transition-[width] duration-200',
                    showJoinedLabel && 'w-[50px]',
                    enableSignOff && isHovered && 'w-[123px]',
                  )}
                >
                  {enableSignOff && isHovered ? t('leave_clubhouse') : t('joined')}
                </span>
              </span>
              <span
                className={tw(
                  'hidden h-9 shrink-0 flex-row items-center justify-center',
                  enableSignOff && 'group-hover:flex',
                )}
              >
                <span
                  className={tw(
                    'w-[50px]',
                    'inline-block overflow-hidden transition-[width] duration-1000',
                    enableSignOff && 'w-[123px]',
                  )}
                />
              </span>
            </motion.div>
          </Button>
        ) : null}
        {isJoined && !justJoined ? (
          <ClubhouseSettingsPopover handleLeave={handleClick} clubhouseSlug={slug} />
        ) : null}
      </div>
    </AnimatePresence>
  )
}
